import * as React from "react"

import CreatedAtChip from "@podcastsoundboard/ps-lib/components/CreatedAtChip/CreatedAtChip"

import searchSoundFiles from "../../../../api/soundfiles/search"
import HeadingContainer from "../../../../components/HeadingContainer"
import bitsToBytesString from "../../../../helpers/soundFile/bitsToBytesString"
import downloadSoundFile from "../../../../helpers/soundFile/downloadSoundFile"
import SoundFile from "../../../../types/SoundFile"
import AddSoundFile from "./components/AddSoundFile"
import EditSoundFileModal from "./components/EditSoundFileModal"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import DownloadIcon from "@podcastsoundboard/ps-lib/ui/icons/Download"
import SettingsIcon from "@podcastsoundboard/ps-lib/ui/icons/Settings"

export default function SoundFileManagement() {
  const [soundFileToEdit, setSoundFileToEdit] =
    React.useState<SoundFile | null>(null)
  const [editSoundFileModalShown, setEditSoundFileModalShown] =
    React.useState(false)

  const [soundFilesLoading, setSoundFilesLoading] = React.useState(true)
  const [soundFiles, setSoundFiles] = React.useState<SoundFile[] | null>(null)
  const [errorMessage, setErrorMessage] = React.useState("")

  const handleSearchSoundFiles = () => {
    setSoundFilesLoading(true)
    searchSoundFiles()
      .then((soundFiles) => {
        setSoundFiles(soundFiles)
        setSoundFilesLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setSoundFilesLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }

  React.useEffect(() => {
    handleSearchSoundFiles()
  }, [])

  return (
    <>
      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert
            className="rounded-none"
            severity="error"
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <HeadingContainer className="mb-8">
        <Typography component="h1" variant="h6" color="textPrimary" noWrap>
          Sound Files
        </Typography>

        <AddSoundFile onCreate={handleSearchSoundFiles} />
      </HeadingContainer>

      {soundFileToEdit && (
        <EditSoundFileModal
          open={editSoundFileModalShown}
          soundFile={soundFileToEdit}
          handleClose={() => setEditSoundFileModalShown(false)}
          onUpdate={() => {
            setEditSoundFileModalShown(false)
            handleSearchSoundFiles()
          }}
        />
      )}

      {soundFilesLoading && (
        <Box className="flex justify-center">
          <CircularProgress />
        </Box>
      )}

      {!soundFilesLoading && (
        <Container maxWidth="md">
          <Stack
            direction="column"
            alignItems="stretch"
            justifyContent="flex-start"
            spacing={1}
          >
            {soundFiles &&
              soundFiles.map((soundFile) => (
                <Paper elevation={4} className="p-4" key={soundFile.uuid}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "100%" }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="0.5rem"
                  >
                    <Stack direction="column" gap="0.5rem">
                      <Typography variant="body1">
                        {soundFile.name || <i>None</i>}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        gap="0.5rem"
                        flexWrap="wrap"
                      >
                        <CreatedAtChip createdAt={soundFile.createdAt} />
                        <Chip
                          size="small"
                          label={
                            soundFile.fileSizeBytes != null ? (
                              bitsToBytesString(soundFile.fileSizeBytes)
                            ) : (
                              <>?KB</>
                            )
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {soundFile.filename || <i>None</i>}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent={"flex-end"}
                      flexGrow={1}
                    >
                      <Tooltip title="Download">
                        <IconButton
                          aria-label="Download"
                          disabled={!soundFile.fileUrl}
                          onClick={() => downloadSoundFile(soundFile)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Settings" className="mt-0">
                        <IconButton
                          className="mt-0"
                          onClick={() => {
                            setSoundFileToEdit(soundFile)
                            setEditSoundFileModalShown(true)
                          }}
                          aria-label="Settings"
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
          </Stack>
        </Container>
      )}
    </>
  )
}
